import { createToast } from '@/utils'
import type { FC } from 'react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import type { PopoverProps } from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { Language, partialUpdateUser, type PartialUser, type UserProfile } from '@/api'
import { useAuth } from '@/hooks/use-auth'
import useTranslate from '@/hooks/use-translate'
import useUserLanguage from '@/hooks/use-user-language'
import { densityPx } from '@/theme/base/density'

export type PlatformLanguage = 'en' | 'fr' | 'de'

const LANGUAGE_FLAG: Record<PlatformLanguage, string> = {
  en: '/assets/flags/flag-uk.svg',
  fr: '/assets/flags/flag-fr.svg',
  de: '/assets/flags/flag-de.svg',
}

interface LanguageMenuBodyProps {
  /** Ex. fermer le Popover MUI parent après sélection */
  onAfterSelect?: () => void
}

export const LanguageMenuBody: FC<LanguageMenuBodyProps> = ({
  onAfterSelect,
}) => {
  const { i18n } = useTranslation()
  const { t } = useTranslate('common')
  const languageEnum = useUserLanguage()
  const { user, updateUserContext } = useAuth()

  const handleChange = useCallback(
    (language: PlatformLanguage) => {
      onAfterSelect?.()
      void i18n.changeLanguage(language)
      localStorage.setItem('language', language)
      createToast('success', t('languageChanged'))
      if (!user?.uid) return
      const profile: UserProfile = {
        ...user.profile,
        language: language as Language,
      }
      const updated: PartialUser = { ...user, profile }
      void partialUpdateUser(user.uid, updated)
        .then(() => updateUserContext(updated))
        .catch(() => undefined)
    },
    [onAfterSelect, i18n, t, updateUserContext, user]
  )

  return (
    <>
      {languageEnum.map((option) => {
        const code = option.value as PlatformLanguage
        const flagSrc = LANGUAGE_FLAG[code]

        return (
          <MenuItem
            key={code}
            data-testid={`language-popover-item-${code}`}
            onClick={() => handleChange(code)}
          >
            <ListItemIcon>
              <Box
                sx={{
                  width: densityPx(28),
                  '& img': {
                    width: '100%',
                  },
                }}
              >
                <img alt={option.label} src={flagSrc} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle2">{option.label}</Typography>
              }
            />
          </MenuItem>
        )
      })}
    </>
  )
}

const defaultAnchorOrigin: PopoverProps['anchorOrigin'] = {
  horizontal: 'right',
  vertical: 'bottom',
}

const defaultTransformOrigin: PopoverProps['transformOrigin'] = {
  horizontal: 'right',
  vertical: 'top',
}

interface LanguagePopoverProps {
  anchorEl: null | Element
  onClose?: () => void
  open?: boolean
  anchorOrigin?: PopoverProps['anchorOrigin']
  transformOrigin?: PopoverProps['transformOrigin']
  /** Fusionné avec le Paper interne (ex. onMouseEnter / onMouseLeave pour hover) */
  PaperProps?: PopoverProps['PaperProps']
}

export const LanguagePopover: FC<LanguagePopoverProps> = (props) => {
  const {
    anchorEl,
    onClose,
    open = false,
    anchorOrigin = defaultAnchorOrigin,
    transformOrigin = defaultTransformOrigin,
    PaperProps: paperPropsProp,
    ...other
  } = props

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      disableScrollLock
      transformOrigin={transformOrigin}
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: { width: densityPx(220), py: 0.5 },
        ...paperPropsProp,
      }}
      {...other}
    >
      <LanguageMenuBody onAfterSelect={onClose} />
    </Popover>
  )
}
